/**
 * LeftShield Admin - App Root Component
 *
 * Single-tenant platform administration app for Transformr staff
 * to manage customer tenants, trials, and platform operations.
 */
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { MainLayout } from './components/layout';
import { HomePage } from './pages/HomePage';
import { NotFoundPage } from './pages/NotFoundPage';
import { TenantsPage } from './pages/TenantsPage';
import { TrialsPage } from './pages/TrialsPage';
import { IdentityPage } from './pages/IdentityPage';
import { MetricsPage } from './pages/MetricsPage';
import { AuditPage } from './pages/AuditPage';
import { SettingsPage } from './pages/SettingsPage';
import { AuthenticatedRoute, useAuth } from './features/authentication';
import { useApiSetup } from './hooks';

// Import global styles
import './styles/teams-theme.css';
import './styles/fluent-overrides.css';

// React Query client configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// =============================================================================
// NAVIGATION CONFIGURATION
// =============================================================================
import {
  HomeRegular,
  HomeFilled,
  BuildingRegular,
  BuildingFilled,
  ClockRegular,
  ClockFilled,
  PeopleRegular,
  PeopleFilled,
  DataUsageRegular,
  DataUsageFilled,
  HistoryRegular,
  HistoryFilled,
  SettingsRegular,
  SettingsFilled,
  ShieldCheckmarkRegular,
} from '@fluentui/react-icons';

// Main navigation items for platform administration
const navItems = [
  {
    path: '/',
    label: 'Dashboard',
    icon: HomeRegular,
    activeIcon: HomeFilled,
  },
  {
    path: '/tenants',
    label: 'Tenants',
    icon: BuildingRegular,
    activeIcon: BuildingFilled,
  },
  {
    path: '/trials',
    label: 'Trials',
    icon: ClockRegular,
    activeIcon: ClockFilled,
  },
  {
    path: '/identity',
    label: 'Platform Users',
    icon: PeopleRegular,
    activeIcon: PeopleFilled,
  },
];

// Admin sub-menu items for system administration
const adminItems = [
  {
    path: '/metrics',
    label: 'Metrics',
    icon: DataUsageRegular,
    activeIcon: DataUsageFilled,
  },
  {
    path: '/audit',
    label: 'Audit Log',
    icon: HistoryRegular,
    activeIcon: HistoryFilled,
  },
  {
    path: '/settings',
    label: 'Settings',
    icon: SettingsRegular,
    activeIcon: SettingsFilled,
  },
];

// =============================================================================
// AUTHENTICATED APP CONTENT
// =============================================================================

/**
 * Inner app content that requires authentication
 */
function AuthenticatedApp() {
  const { user, signOut } = useAuth();
  const isAdmin = true; // All users of this app are platform admins

  // Initialize API with authentication token provider
  useApiSetup();

  // Map auth user to layout user format
  const layoutUser = {
    displayName: user?.displayName || 'Unknown User',
    email: user?.email || '',
  };

  return (
    <Router>
      <Routes>
        {/* Routes with MainLayout (authenticated) */}
        <Route
          element={
            <MainLayout
              productName="LeftShield Admin"
              LogoIcon={ShieldCheckmarkRegular}
              navItems={navItems}
              showAdminSection={isAdmin}
              adminItems={adminItems}
              user={layoutUser}
              userRole="Platform Admin"
              roleBadgeColor="danger"
              onSignOut={signOut}
            />
          }
        >
          <Route path="/" element={<HomePage />} />
          <Route path="/tenants" element={<TenantsPage />} />
          <Route path="/trials" element={<TrialsPage />} />
          <Route path="/identity" element={<IdentityPage />} />
          <Route path="/metrics" element={<MetricsPage />} />
          <Route path="/audit" element={<AuditPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Router>
  );
}

// =============================================================================
// APP COMPONENT
// =============================================================================
function App() {
  return (
    <FluentProvider theme={webLightTheme}>
      <QueryClientProvider client={queryClient}>
        <AuthenticatedRoute>
          <AuthenticatedApp />
        </AuthenticatedRoute>
      </QueryClientProvider>
    </FluentProvider>
  );
}

export default App;
